/**
 * Schema.org Structured Data Component
 * 
 * Generates JSON-LD structured data for SEO pages.
 * Supports: JobPosting, LocalBusiness, Service
 * 
 * RULES:
 * - Schema matches page language
 * - No mixed-language values
 * - JSON-LD only (not microdata)
 * - Enables rich snippets eligibility
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * JobPosting Schema (Schema.org)
 * For: /jobs/{country}, /jobs/{country}/{category}
 */
export const JobPostingSchema = ({
  title,
  description,
  companyName,
  companyLogo,
  country,
  city,
  employmentType = 'TEMPORARY',
  datePosted,
  validThrough,
  salary,
  currency = 'EUR',
  language = 'en'
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: title,
    description: description,
    identifier: {
      '@type': 'PropertyValue',
      name: companyName,
      value: `${country}-${city}-${title}`.toLowerCase().replace(/\s+/g, '-')
    },
    datePosted: datePosted || new Date().toISOString().split('T')[0],
    validThrough: validThrough || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    employmentType: employmentType,
    hiringOrganization: {
      '@type': 'Organization',
      name: companyName,
      logo: companyLogo
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: city,
        addressCountry: country?.toUpperCase()?.slice(0, 2) || 'FI'
      }
    },
    inLanguage: language
  };

  // Add salary if provided
  if (salary) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: currency,
      value: {
        '@type': 'QuantitativeValue',
        value: salary,
        unitText: 'DAY'
      }
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * LocalBusiness Schema (Schema.org)
 * For: /services/{country}/{city}, /autoremont/{city}
 */
export const LocalBusinessSchema = ({
  name,
  description,
  serviceType,
  country,
  city,
  streetAddress,
  postalCode,
  telephone,
  email,
  url,
  priceRange,
  latitude,
  longitude,
  openingHours,
  language = 'en'
}) => {
  const countryCodeMap = {
    'finland': 'FI', 'estonia': 'EE', 'sweden': 'SE',
    'norway': 'NO', 'germany': 'DE', 'denmark': 'DK'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: name,
    description: description,
    '@id': url,
    url: url,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressCountry: countryCodeMap[country?.toLowerCase()] || 'FI'
    },
    areaServed: {
      '@type': 'City',
      name: city
    },
    inLanguage: language
  };

  // Add optional fields if provided
  if (streetAddress) schema.address.streetAddress = streetAddress;
  if (postalCode) schema.address.postalCode = postalCode;
  if (telephone) schema.telephone = telephone;
  if (email) schema.email = email;
  if (priceRange) schema.priceRange = priceRange;
  
  if (latitude && longitude) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: latitude,
      longitude: longitude
    };
  }

  if (openingHours) {
    schema.openingHoursSpecification = {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00'
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * Service Schema (Schema.org)
 * For: /services/{country}/{city}/{service}
 */
export const ServiceSchema = ({
  name,
  description,
  serviceType,
  provider,
  areaServed,
  country,
  city,
  url,
  priceRange,
  language = 'en'
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: name,
    description: description,
    serviceType: serviceType,
    url: url,
    areaServed: {
      '@type': 'City',
      name: city || areaServed
    },
    inLanguage: language
  };

  if (provider) {
    schema.provider = {
      '@type': 'Organization',
      name: provider
    };
  }

  if (priceRange) {
    schema.offers = {
      '@type': 'Offer',
      priceSpecification: {
        '@type': 'PriceSpecification',
        priceCurrency: 'EUR',
        price: priceRange
      }
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * BreadcrumbList Schema (Schema.org)
 * For: All SEO pages
 */
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * Organization Schema (Schema.org)
 * For: Homepage, about pages
 */
export const OrganizationSchema = ({
  name = 'KEIKKA',
  url = 'https://keikka.com',
  logo = 'https://keikka.com/logo.png',
  description,
  sameAs = []
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: name,
    url: url,
    logo: logo,
    description: description || 'Global work and services marketplace',
    sameAs: sameAs
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * WebPage Schema (Schema.org)
 * For: All pages
 */
export const WebPageSchema = ({
  name,
  description,
  url,
  inLanguage = 'en',
  datePublished,
  dateModified
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: name,
    description: description,
    url: url,
    inLanguage: inLanguage,
    datePublished: datePublished || new Date().toISOString(),
    dateModified: dateModified || new Date().toISOString()
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * ItemList Schema for category pages
 */
export const ItemListSchema = ({ items, name, description, url }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: name,
    description: description,
    url: url,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: item.url,
      name: item.name
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default {
  JobPostingSchema,
  LocalBusinessSchema,
  ServiceSchema,
  BreadcrumbSchema,
  OrganizationSchema,
  WebPageSchema,
  ItemListSchema
};
